import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Input, FormGroup, Button, Label, Card, CardBody, CardTitle, InputGroup, InputGroupText } from "reactstrap";
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../Utils/Baseurl/Baseurl";


interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

interface ErrorResponse {
  message?: string;
}

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordForm>();

  const validatePassword = (password: string) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
    return passwordRegex.test(password);
  };


  const onSubmit = async (data: ResetPasswordForm) => {
    console.log("Submitted Data:", data);
    try {
      const response = await axios.post(`${baseUrl}user/resetpassword/${token}`, {
        newPassword: data.newPassword,
      });

      if (response.data.success) {
        toast.success("Password reset successfully!");
        navigate("/login");
      } else {
        toast.error(response.data.message || "Failed to reset password.");
      }
    } catch (error) {
      console.error("Error:", error);
      const axiosError = error as AxiosError<ErrorResponse>;
      toast.error(axiosError.response?.data?.message || "An error occurred while resetting the password.");
    }
  };


  return (
    <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
      <Card style={{ width: "400px", boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)" }}>
        <CardBody>
          <CardTitle tag="h3" className="text-center text-primary mb-4">
            Reset Your Password
          </CardTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* New Password Field */}
            <FormGroup>
              <Label>New Password</Label>
              <InputGroup>
                <Controller
                  name="newPassword"
                  control={control}
                  rules={{
                    required: "Please enter a new password",
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
                      message:
                        "Password must be at least 6 characters long, contain an uppercase letter, a lowercase letter, a number, and a special character",
                    },
                  }}
                  render={({ field }) => {
                    console.log("Password Input:", field.value); // Debugging
                    return (
                      <Input
                        type={showPassword ? "text" : "password"}
                        {...field}
                        placeholder="New Password"
                      />
                    );
                  }}
                />
                <InputGroupText
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  👁️
                </InputGroupText>
              </InputGroup>
              {errors.newPassword?.message && (
                <div className="text-danger">{errors.newPassword.message}</div>
              )}
            </FormGroup>


            {/* Confirm Password Field */}
            <FormGroup>
              <Label>Confirm Password</Label>
              <InputGroup>
                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: "Please confirm your password",
                    validate: (value) => value === watch("newPassword") || "Passwords do not match",
                  }}
                  render={({ field }) => (
                    <Input type={showConfirmPassword ? "text" : "password"} {...field} placeholder="Confirm Password" />
                  )}
                />
                <InputGroupText
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                </InputGroupText>
              </InputGroup>
              {errors.confirmPassword?.message && <div className="text-danger">{errors.confirmPassword.message}</div>}
            </FormGroup>

            <Button type="submit" color="primary" className="w-100 mt-3">
              Reset Password
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

